// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import axiosApi from "core/request";
import dayjs, { Dayjs } from "dayjs";
import { useAppSelector, useLocalStorage } from "hooks";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { sendNotification } from "ui/Toast";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Button } from "@mui/material";

import VacationConflictModal from "../../../../features/VacationConflictModal";
import Loader from "../../../../ui/Loader";
import { Text } from "../../../../ui/Text/Text";

import { VacationCard } from "./components/VacationCard";

import styles from "./styles.module.scss";

interface CalendarData {
  allDay: boolean | null;
  description: string | null;
  endDate: string;
  id: number | null;
  startDate: string | null;
  title: string | null;
  type: number | null;
  isRequest: boolean | null;
  pureDays?: number;
}

export const Vacations = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const employeeId = useAppSelector((state) => state.auth.userId);
  const [isLoading, setIsLoading] = useState(false);
  const [holidaysToDelete, setHolidaysToDelete] = useState<number[]>([]);
  const [calendarData, setCalendarData] = useState<CalendarData[]>([]);

  const [addedHolidays, setAddedHolidays] = useState<
    {
      id: string | null;
      date: Dayjs | null;
      dateEnd: Dayjs | null;
      note: string;
    }[]
  >([]);

  const [year, setYear] = useLocalStorage<number>(
    "vacationsSickDaysFilterYear",
    currentDate.getFullYear(),
  );

  const [metaInfo, setMetaInfo] = useState({
    maxHolidaysPerYear: 0,
    isHolidaysModifiable: false,
    isVacationModifiable: false,
    maxVacationDaysPerYear: 28,
    vacationBalance: 0,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [initialListOfDates, setInitialListOfDates] = useState([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const calculateFilledDatesFromPureDays = () => {
    return [...calendarData, ...addedHolidays]
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) => t.startDate === item.startDate && t.endDate === item.endDate,
          ),
      )
      .reduce((acc, item) => {
        return acc + item.pureDays;
      }, 0);
  };

  const handleChangeYear = (year: number | undefined): void => {
    if (year === undefined) {
      return;
    }
    setYear(year);
  };

  const filledDates = calculateFilledDatesFromPureDays();

  const yearToFetch = `${year}.01.01`;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [calendarRes, metaRes] = await Promise.all([
        axiosApi.get("/personal-cabinet/calendar", {
          params: { employeeId, year, type: 1 },
        }),
        axiosApi.get(`/personal-cabinet/calendar/meta`, {
          params: { employeeId, date: yearToFetch },
        }),
      ]);

      setCalendarData(calendarRes.data);
      setMetaInfo(metaRes.data);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [employeeId, year]);

  const handleSave = async () => {
    try {
      const allDates = [...calendarData, ...addedHolidays];

      if (allDates.length !== 0) {
        const hasLongVacation = allDates?.some(
          (d) =>
            dayjs(typeof d.id === "string" ? d.dateEnd : d.endDate)?.diff(
              dayjs(typeof d.id === "string" ? d.date : d.startDate),
              "day",
            ) +
            1 >=
            14,
        );

        if (!hasLongVacation) {
          throw new Error(t("vacationsSickDays.vacationDurationError"));
        }
      }

      setIsLoading(true);

      const daysOffDto = addedHolidays.map((holiday) => {
        const localStartDate = dayjs(holiday.date).startOf("day");
        const localEndDate = dayjs(holiday.dateEnd).startOf("day");
        const offset = localStartDate.utcOffset();
        const offsetInHours = dayjs().utcOffset() / 60;

        const adjustedDate = localStartDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        const adjustedEndDate = localEndDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        return {
          startDate: adjustedDate,
          finishDate: adjustedEndDate,
          sign: 1,
          remark: holiday.note || null,
          pureDays: holiday.pureDays,
        };
      });

      if (holidaysToDelete.length > 0) {
        await axiosApi.delete("/personal-cabinet/calendar/daysoff", {
          data: { ids: holidaysToDelete },
        });
      }
      if (daysOffDto.length > 0) {
        const payload = {
          employeeId,
          daysOffDto,
        };

        await axiosApi.post("/personal-cabinet/calendar/daysoff", payload);
      }

      sendNotification(t("notifications.successfullySaved"), "success");

      setAddedHolidays([]);
      setHolidaysToDelete([]);
      setIsEdit(false);
      fetchData();
    } catch (error) {
      if (typeof error.message === "string") {
        sendNotification(error.message, "error");
        return;
      }
      sendNotification(t("statuses.Error"), "error");
      console.error("Error saving vacations data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewHoliday = (holiday: {
    date: Dayjs | null;
    dateEnd: Dayjs | null;
    note: string;
    id: string | null;
    pureDays?: number;
  }) => {
    setAddedHolidays((prev) => [...prev, holiday]);
  };

  const handleMarkForDelete = (id: number | string) => {
    // @ts-ignore
    setHolidaysToDelete((prev) => [...prev, id]);
    setCalendarData((prev) => prev.filter((item) => item.id !== id));
    setAddedHolidays((prev) => prev.filter((holiday) => holiday.id !== id));
  };

  const handleRemoveAddedHoliday = (id: string | null) => {
    setAddedHolidays((prev) => prev.filter((holiday) => holiday.id !== id));
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div>
      {!isLoading && (
        <div className={styles.meta_info}>

          <div className={styles.box_year}>
            <ArrowLeftIcon
              htmlColor="gray"
              sx={{ color: "green", fontSize: "40px" }}
              onClick={() => handleChangeYear(year - 1)}
            />
            <p>{year}</p>
            <ArrowRightIcon
              htmlColor="gray"
              sx={{
                color: "green",
                fontSize: "40px",
              }}
              onClick={() => handleChangeYear(year + 1)}
            />
          </div>

          <div style={{ marginLeft: "50px" }} className={styles.meta_info}>
            {metaInfo.isVacationModifiable && (
              <Button
                disabled={isLoading}
                variant="contained"
                onClick={isEdit ? handleSave : () => setIsEdit(!isEdit)}
              >
                {isEdit ? t("buttons.Save") : t("buttons.Edit")}
              </Button>
            )}
            {isEdit && (
              <Button
                disabled={isLoading}
                variant="contained"
                onClick={() => {
                  setIsEdit(!isEdit);
                  setAddedHolidays([]);
                  setHolidaysToDelete([]);
                  fetchData();
                }}
              >
                {t("buttons.Close")}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className={styles.cards_container}>
        {isLoading ? (
          <div
            style={{
              width: "100%",
            }}
          >
            <Loader />
          </div>
        ) : isEdit ? (
          <>
            {metaInfo.isVacationModifiable && (
              <VacationCard
                maxVacationDaysPerYear={metaInfo.maxVacationDaysPerYear}
                filledDates={filledDates}
                addedHolidays={addedHolidays}
                isNew
                onAdd={handleAddNewHoliday}
                allDates={[...calendarData, ...addedHolidays]}
                initialListOfDates={initialListOfDates}
              />
            )}

            {[...calendarData, ...addedHolidays].map((item: any) => (
              <VacationCard
                dateEnd={
                  typeof item.id === "number" ? item.endDate : item.dateEnd
                }
                key={item.id || item.id}
                id={item.id || null}
                date={typeof item.id === "string" ? item.date : item.startDate}
                note={
                  typeof item.id === "string" ? item.note : item.description
                }
                onDelete={
                  typeof item.id === "number"
                    ? () => handleMarkForDelete(item.id!)
                    : () => handleRemoveAddedHoliday(item.id)
                }
              />
            ))}
          </>
        ) : (
          <div>
            <div className={styles.summary_table}>
              <table className={styles.summary_table_content}>
                <tbody>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("account.yearStartBalance")}
                    </th>
                    <td>
                      <span className={styles.value_color}>
                        {metaInfo.vacationBalance}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("account.paidVacation")}
                    </th>
                    <td>
                      <span className={styles.value_color}>28</span>
                    </td>
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("account.filled-in")}
                    </th>
                    <td>
                      <span className={styles.value_color}>
                        {filledDates} {t("account.of")} {metaInfo.maxVacationDaysPerYear}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.sick_leave_table}>
              <table className={styles.sick_leave_table_content}>
                <thead className={styles.table_header}>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.dates")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.daysCount")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.note")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calendarData
                    .filter(
                      (item, index, self) =>
                        index ===
                        self.findIndex(
                          (t) =>
                            t.startDate === item.startDate &&
                            t.endDate === item.endDate,
                        ),
                    )
                    .map((item: CalendarData, idx) => {
                      const startDate = moment(item.startDate);
                      const endDate = moment(item.endDate);
                      const isExpanded = expandedRows.has(item.id!);
                      const description =
                        item.description ||
                        String(t("vacationsSickDays.noReason"));
                      const isLongText = description.length > 50;

                      return (
                        <tr key={item.id}>
                          <td>
                            {startDate.format("DD.MM.YYYY")} -{" "}
                            {endDate.format("DD.MM.YYYY")}
                          </td>
                          <td>{item.pureDays || 0}</td>
                          <td className={isExpanded ? styles.expanded_cell : ""}>
                            {isExpanded ? (
                              <div>
                                <div className={styles.full_text}>
                                  {description}
                                </div>
                                <button
                                  className={styles.collapse_button}
                                  onClick={() => toggleRowExpansion(item.id!)}
                                >
                                  Свернуть
                                </button>
                              </div>
                            ) : (
                              <div>
                                <div className={styles.truncated_text}>
                                  {description.length > 50
                                    ? `${description.substring(0, 50)}...`
                                    : description}
                                </div>
                                {isLongText && (
                                  <button
                                    className={styles.expand_button}
                                    onClick={() => toggleRowExpansion(item.id!)}
                                  >
                                    Смотреть полностью
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {calendarData.length === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.no_data}>
                        {t("vacationsSickDays.noVacationData")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
